import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, useSearchParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { AuthProvider } from "@/hooks/useAuth";
import { SensitiveValuesProvider } from "@/hooks/useSensitiveValues";
import { ThemeProvider } from "@/hooks/useTheme";
import ProtectedRoute from "@/components/ProtectedRoute";

const Layout = lazy(() => import("./components/Layout"));
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const OAuthPopup = lazy(() => import("./pages/OAuthPopup"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Accounts = lazy(() => import("./pages/Accounts"));
const AccountDetail = lazy(() => import("./pages/AccountDetail"));
const Cards = lazy(() => import("./pages/Cards"));
const CardDetail = lazy(() => import("./pages/CardDetail"));
const Categories = lazy(() => import("./pages/Categories"));
const Goals = lazy(() => import("./pages/Goals"));
const Settings = lazy(() => import("./pages/Settings"));
const Planning = lazy(() => import("./pages/Planning"));
const Calculadoras = lazy(() => import("./pages/Calculadoras"));
const Investments = lazy(() => import("./pages/Investments"));
const InvestmentDetail = lazy(() => import("./pages/InvestmentDetail"));
const InstitutionDetail = lazy(() => import("./pages/InstitutionDetail"));
const Receivables = lazy(() => import("./pages/Receivables"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BrandPreview = lazy(() => import("./pages/BrandPreview"));
const TermsAcceptance = lazy(() => import("./pages/TermsAcceptance"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotaLegal = lazy(() => import("./pages/NotaLegal"));
const PoliticaSeguranca = lazy(() => import("./pages/PoliticaSeguranca"));
const PoliticaCookies = lazy(() => import("./pages/PoliticaCookies"));
const ConfiguracoesCookies = lazy(() => import("./pages/ConfiguracoesCookies"));
const EticaConduta = lazy(() => import("./pages/EticaConduta"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AdminPage = lazy(() => import("./pages/Admin"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutResult = lazy(() => import("./pages/CheckoutResult"));
const Renewal = lazy(() => import("./pages/Renewal"));
const LayoutGuide = lazy(() => import("./pages/LayoutGuide"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60_000,       // 2 min — data is fresh, no refetch on remount
      gcTime: 10 * 60_000,         // 10 min — keep cache in memory
      refetchOnWindowFocus: false,  // don't refetch every time user returns to tab
      retry: 2,                     // reduce retries (default is 3)
    },
  },
});

function RouteLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-3xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
        <div className="mt-4 h-8 w-40 animate-pulse rounded-full bg-muted" />
        <div className="mt-6 space-y-3">
          <div className="h-12 animate-pulse rounded-2xl bg-muted" />
          <div className="h-12 animate-pulse rounded-2xl bg-muted" />
          <div className="h-12 animate-pulse rounded-2xl bg-muted" />
        </div>
      </div>
    </div>
  );
}

function AppHome() {
  const [searchParams] = useSearchParams();

  if (searchParams.get("oauth") === "1") {
    return <OAuthPopup />;
  }

  if (import.meta.env.DEV && searchParams.get("guide") === "1") {
    return <LayoutGuide />;
  }

  return <Landing />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SensitiveValuesProvider>
      <AuthProvider>
        <ThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename="/app">
              <AppErrorBoundary>
                <Suspense fallback={<RouteLoader />}>
                  <Routes>
                    <Route path="/" element={<AppHome />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/checkout/resultado" element={<CheckoutResult />} />
                    <Route path="/renovar" element={<Renewal />} />
                    {import.meta.env.DEV && (
                      <>
                        <Route path="/brand-preview" element={<BrandPreview />} />
                        <Route path="/layout-guide" element={<LayoutGuide />} />
                      </>
                    )}
                    <Route path="/termo-de-uso" element={<TermsOfService />} />
                    <Route path="/termos-servico" element={<TermsOfService />} />
                    <Route path="/termos-de-uso" element={<TermsOfService />} />
                    <Route path="/termos" element={<TermsOfService />} />
                    <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
                    <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
                    <Route path="/privacidade" element={<PrivacyPolicy />} />
                    <Route path="/nota-legal" element={<NotaLegal />} />
                    <Route path="/politica-de-seguranca" element={<PoliticaSeguranca />} />
                    <Route path="/seguranca" element={<PoliticaSeguranca />} />
                    <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
                    <Route path="/cookies" element={<PoliticaCookies />} />
                    <Route path="/configuracoes-de-cookies" element={<ConfiguracoesCookies />} />
                    <Route path="/etica-e-conduta" element={<EticaConduta />} />
                    <Route path="/etica" element={<EticaConduta />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    <Route
                      path="/aceite"
                      element={(
                        <ProtectedRoute requireTermsAcceptance={false}>
                          <TermsAcceptance />
                        </ProtectedRoute>
                      )}
                    />
                    <Route
                      element={(
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      )}
                    >
                      <Route path="/dashboard" element={<Index />} />
                      <Route path="/transacoes" element={<Transactions />} />
                      <Route path="/contas" element={<Accounts />} />
                      <Route path="/contas/:id" element={<AccountDetail />} />
                      <Route path="/cartoes" element={<Cards />} />
                      <Route path="/cartoes/:id" element={<CardDetail />} />
                      <Route path="/categorias" element={<Categories />} />
                      <Route path="/investimentos" element={<Investments />} />
                      <Route path="/investimentos/:id" element={<InvestmentDetail />} />
                      <Route path="/investimentos/instituicao/:id" element={<InstitutionDetail />} />
                      <Route path="/planejamento" element={<Planning />} />
                      <Route path="/calculadoras" element={<Calculadoras />} />
                      <Route path="/valores-a-receber" element={<Receivables />} />
                      <Route path="/metas" element={<Goals />} />
                      <Route path="/configuracoes" element={<Settings />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/instalar" element={<Install />} />
                      <Route path="/analytics" element={<Analytics />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </AuthProvider>
    </SensitiveValuesProvider>
  </QueryClientProvider>
);

export default App;
